// frontend/src/pages/dispatch/DispatchManagement.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, LinearProgress, IconButton, Tooltip,
  TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, InputAdornment, Pagination,
  CircularProgress, Alert, Collapse, Popover,
  Dialog, DialogTitle, DialogContent, DialogActions, FormGroup,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import {
  LocalShipping, Visibility, CheckCircle,
  Refresh, FilterList, Clear, Search, Check, Done,
  ContentCopy, ExpandMore, ExpandLess, CalendarMonth,
  FileDownload
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { manufacturingAPI, authAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePageTitle from '../../hooks/usePageTitle';

// Debounce hook for search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

// Auto-expanding textarea component with save indicator
export function DispatchNotesField({ orderId, initialValue, onSave, canEdit = true }) {
  const [value, setValue] = useState(initialValue || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [lastSavedValue, setLastSavedValue] = useState(initialValue || '');
  
  const debouncedValue = useDebounce(value, 800);

  useEffect(() => {
    setValue(initialValue || '');
    setLastSavedValue(initialValue || '');
  }, [initialValue]);

  useEffect(() => {
    if (canEdit && debouncedValue !== lastSavedValue) {
      saveNotes(debouncedValue);
    }
  }, [debouncedValue]);

  const saveNotes = async (notes) => {
    setIsSaving(true);
    setIsSaved(false);
    try {
      await manufacturingAPI.customerOrders.updateDispatchNotes(orderId, notes);
      setLastSavedValue(notes);
      setIsSaved(true);
      if (onSave) onSave(orderId, notes);
      
      setTimeout(() => {
        setIsSaved(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to save dispatch notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    if (!canEdit) return;
    setValue(e.target.value);
    setIsSaved(false);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', minWidth: 150 }}>
      <TextField
        multiline
        minRows={2}
        maxRows={6}
        value={value}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        placeholder={canEdit ? 'Dispatch notes...' : 'View only — no permission to edit'}
        size="small"
        fullWidth
        InputProps={{ readOnly: !canEdit }}
        title={canEdit ? undefined : 'You do not have permission to edit dispatch notes'}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem',
            backgroundColor: canEdit ? 'background.paper' : 'action.disabledBackground',
            '& textarea': {
              resize: 'none',
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isSaved ? 'success.main' : 'divider',
          }
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {isSaving && (
          <CircularProgress size={14} thickness={5} />
        )}
        {isSaved && !isSaving && (
          <Tooltip title="Saved">
            <Check 
              sx={{ 
                fontSize: 16, 
                color: 'success.main',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '50%',
                padding: '2px'
              }} 
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// Component to display consignment and tracking info
function ConsignmentTrackingCell({ consignments }) {
  const [expanded, setExpanded] = useState(false);
  
  if (!consignments || consignments.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        No consignments
      </Typography>
    );
  }

  const activeConsignments = consignments.filter(c => c.status !== 'deleted');
  
  if (activeConsignments.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        No consignments
      </Typography>
    );
  }

  const handleCopyTracking = (trackingNumber, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(trackingNumber);
    toast.success(`Copied: ${trackingNumber}`);
  };

  const handleCopyConsignment = (consignmentNumber, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(consignmentNumber);
    toast.success(`Copied: ${consignmentNumber}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'shipped': return 'success';
      case 'label_printed': return 'warning';
      case 'created': return 'info';
      default: return 'default';
    }
  };

  const allTrackingNumbers = activeConsignments.flatMap(c => c.tracking_numbers || []);
  const shippedConsignments = activeConsignments.filter(c => c.status === 'shipped');

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Chip
          size="small"
          label={`${activeConsignments.length} consignment${activeConsignments.length !== 1 ? 's' : ''}`}
          color={shippedConsignments.length === activeConsignments.length ? 'success' : 'primary'}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        {allTrackingNumbers.length > 0 && (
          <Chip
            size="small"
            label={`${allTrackingNumbers.length} tracking`}
            color="default"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        {activeConsignments.length > 1 && (
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
            sx={{ padding: 0.25 }}
          >
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        )}
      </Box>

      <Box sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: '0.65rem', minWidth: 28 }}
          >
            CON:
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              fontFamily: 'monospace',
              fontWeight: 'bold',
              color: activeConsignments[0].status === 'shipped' ? 'success.main' : 'text.primary'
            }}
          >
            {activeConsignments[0].consignment_number}
          </Typography>
          <Tooltip title="Copy consignment number">
            <IconButton 
              size="small" 
              onClick={(e) => handleCopyConsignment(activeConsignments[0].consignment_number, e)}
              sx={{ padding: 0.25 }}
            >
              <ContentCopy sx={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
          <Chip
            size="small"
            label={activeConsignments[0].status}
            color={getStatusColor(activeConsignments[0].status)}
            sx={{ height: 16, fontSize: '0.65rem' }}
          />
        </Box>
        
        {activeConsignments[0].tracking_numbers?.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: '0.65rem', minWidth: 28, mt: 0.25 }}
            >
              TRK:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {activeConsignments[0].tracking_numbers.slice(0, 2).map((tn, idx) => (
                <Chip
                  key={idx}
                  size="small"
                  label={tn}
                  variant="outlined"
                  onClick={(e) => handleCopyTracking(tn, e)}
                  sx={{ 
                    height: 18, 
                    fontSize: '0.65rem',
                    fontFamily: 'monospace',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                />
              ))}
              {activeConsignments[0].tracking_numbers.length > 2 && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  +{activeConsignments[0].tracking_numbers.length - 2} more
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Collapse in={expanded}>
        {activeConsignments.slice(1).map((consignment, idx) => (
          <Box key={consignment.id} sx={{ mb: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: '0.65rem', minWidth: 28 }}
              >
                CON:
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: consignment.status === 'shipped' ? 'success.main' : 'text.primary'
                }}
              >
                {consignment.consignment_number}
              </Typography>
              <Tooltip title="Copy consignment number">
                <IconButton 
                  size="small" 
                  onClick={(e) => handleCopyConsignment(consignment.consignment_number, e)}
                  sx={{ padding: 0.25 }}
                >
                  <ContentCopy sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
              <Chip
                size="small"
                label={consignment.status}
                color={getStatusColor(consignment.status)}
                sx={{ height: 16, fontSize: '0.65rem' }}
              />
            </Box>
            
            {consignment.tracking_numbers?.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem', minWidth: 28, mt: 0.25 }}
                >
                  TRK:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {consignment.tracking_numbers.slice(0, 2).map((tn, tnIdx) => (
                    <Chip
                      key={tnIdx}
                      size="small"
                      label={tn}
                      variant="outlined"
                      onClick={(e) => handleCopyTracking(tn, e)}
                      sx={{ 
                        height: 18, 
                        fontSize: '0.65rem',
                        fontFamily: 'monospace',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    />
                  ))}
                  {consignment.tracking_numbers.length > 2 && (
                    <Typography variant="caption" color="text.secondary">
                      +{consignment.tracking_numbers.length - 2}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Collapse>
    </Box>
  );
}

// Delivery Service dropdown component
function DeliveryServiceSelect({ orderId, initialValue, onSave }) {
  const [value, setValue] = useState(initialValue || 'ON');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setValue(initialValue || 'ON');
  }, [initialValue]);

  const handleChange = async (event) => {
    event.stopPropagation();
    const newValue = event.target.value;
    setValue(newValue);
    setIsSaving(true);
    setIsSaved(false);

    try {
      await manufacturingAPI.customerOrders.updateDeliveryService(orderId, newValue);
      setIsSaved(true);
      if (onSave) onSave(orderId, newValue);
      
      setTimeout(() => {
        setIsSaved(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to update delivery service:', error);
      toast.error('Failed to update delivery service');
      setValue(initialValue || 'ON');
    } finally {
      setIsSaving(false);
    }
  };

  const getServiceColor = (service) => {
    switch (service) {
      case 'AM': return 'error';
      case '3DAY': return 'info';
      case 'ON': 
      default: return 'success';
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <FormControl size="small" sx={{ minWidth: 80 }}>
        <Select
          value={value}
          onChange={handleChange}
          onClick={(e) => e.stopPropagation()}
          disabled={isSaving}
          sx={{
            height: 28,
            fontSize: '0.75rem',
            '& .MuiSelect-select': {
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isSaved ? 'success.main' : 'divider',
            }
          }}
        >
          <MenuItem value="ON">
            <Chip label="ON" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
          </MenuItem>
          <MenuItem value="AM">
            <Chip label="AM" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
          </MenuItem>
          <MenuItem value="3DAY">
            <Chip label="3DAY" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
          </MenuItem>
        </Select>
      </FormControl>
      {isSaving && (
        <CircularProgress size={14} thickness={5} />
      )}
      {isSaved && !isSaving && (
        <Check sx={{ fontSize: 16, color: 'success.main' }} />
      )}
    </Box>
  );
}

export default function DispatchManagement() {
  const navigate = useNavigate();
  const { hasPageAccess } = useAuth();
  const canEditDispatchNotes = hasPageAccess('edit-dispatch-notes');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [showReadyOnly, setShowReadyOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ready_to_ship');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderNumberQuery, setOrderNumberQuery] = useState('');

  // Dispatch date filter
  const [shippedDateFilter, setShippedDateFilter] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  // Due date filter (for ready to ship)
  const [dueDateFilter, setDueDateFilter] = useState(null);
  const [dueDateAnchorEl, setDueDateAnchorEl] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Sort for complete tab
  const [completeSortBy, setCompleteSortBy] = useState('order_date');
  
  // Pagination - restore from sessionStorage on mount
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem('dispatch_page');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(25);
  
  // Debounced search
  const debouncedSearch = useDebounce(searchQuery, 500);
  const debouncedOrderNumber = useDebounce(orderNumberQuery, 500);
  
  const [stats, setStats] = useState({
    total_active: 0,
    ready_to_ship: 0,
    in_production: 0,
    partially_shipped: 0,
    overdue: 0,
    complete: 0
  });

  // Export dialog state
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const EXPORT_COLUMNS = [
    { key: 'customer',        label: 'Customer' },
    { key: 'due_date',        label: 'Due Date' },
    { key: 'completed_date',  label: 'Shipped Date' },
    { key: 'status',          label: 'Status' },
    { key: 'service',         label: 'Service' },
    { key: 'delivery_name',   label: 'Delivery Name' },
    { key: 'postcode',        label: 'Postcode' },
    { key: 'product_code',    label: 'Product Code' },
    { key: 'product_name',    label: 'Product Name' },
    { key: 'qty_ordered',     label: 'Qty Ordered' },
    { key: 'qty_shipped',     label: 'Qty Shipped' },
    { key: 'qty_remaining',   label: 'Qty Remaining' },
    { key: 'line_weight',     label: 'Line Weight (kg)' },
    { key: 'unit_value',      label: 'Unit Value' },
    { key: 'line_value',      label: 'Line Value' },
    { key: 'dispatch_notes',  label: 'Dispatch Notes' },
  ];
  const DEFAULT_EXPORT_STATUSES = {
    new: false,
    confirmed: false,
    in_production: true,
    ready_to_ship: true,
    partially_shipped: true,
    complete: false,
    cancelled: false,
  };
  const DEFAULT_EXPORT_COLUMNS = EXPORT_COLUMNS.reduce((acc, c) => {
    acc[c.key] = true;
    return acc;
  }, {});

  const [exportOpen, setExportOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(todayStr);
  const [exportEndDate, setExportEndDate] = useState(todayStr);
  const [exportStatuses, setExportStatuses] = useState(DEFAULT_EXPORT_STATUSES);
  const [exportIncludeOverdue, setExportIncludeOverdue] = useState(false);
  const [exportColumns, setExportColumns] = useState(DEFAULT_EXPORT_COLUMNS);
  const [exportDateMode, setExportDateMode] = useState('due_date');
  const [exporting, setExporting] = useState(false);

  // Scroll position restore
  const scrollRestoredRef = useRef(false);

  usePageTitle('Dispatch');

  const saveScrollPosition = () => {
    sessionStorage.setItem('dispatch_scroll', String(window.scrollY));
    sessionStorage.setItem('dispatch_page', String(page));
  };

  // Restore scroll position after orders load
  useEffect(() => {
    if (!loading && orders.length > 0 && !scrollRestoredRef.current) {
      scrollRestoredRef.current = true;
      const saved = sessionStorage.getItem('dispatch_scroll');
      if (saved) {
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(saved, 10));
        });
      }
    }
  }, [loading, orders]);

  // Load saved due date preference on mount
  useEffect(() => {
    authAPI.getPreferences().then(res => {
      const prefs = res.data;
      if (prefs.dispatchDueDateFilter) {
        const saved = new Date(prefs.dispatchDueDateFilter);
        if (!isNaN(saved.getTime())) {
          setDueDateFilter(saved);
        }
      }
      if (prefs.dispatchCompleteSortBy) {
        setCompleteSortBy(prefs.dispatchCompleteSortBy);
      }
      const exp = prefs.dispatchExport;
      if (exp && typeof exp === 'object') {
        if (exp.statuses && typeof exp.statuses === 'object') {
          setExportStatuses(prev => ({ ...prev, ...exp.statuses }));
        }
        if (exp.columns && typeof exp.columns === 'object') {
          setExportColumns(prev => ({ ...prev, ...exp.columns }));
        }
        if (typeof exp.include_overdue === 'boolean') {
          setExportIncludeOverdue(exp.include_overdue);
        }
        if (exp.date_mode === 'due_date' || exp.date_mode === 'completed_date') {
          setExportDateMode(exp.date_mode);
        }
      }
    }).catch(() => {}).finally(() => {
      setPrefsLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!prefsLoaded) return;
    loadOrders();
  }, [prefsLoaded, page, debouncedSearch, debouncedOrderNumber, statusFilter, showReadyOnly, shippedDateFilter, dueDateFilter, completeSortBy]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page: page,
        page_size: pageSize
      };

      if (statusFilter !== 'complete') {
        params.hide_complete = 'true';
      }

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      if (debouncedOrderNumber.trim()) {
        params.order_number = debouncedOrderNumber.trim();
      }

      if (statusFilter === 'ready_to_ship') {
        // Don't filter by database status - use fully_allocated instead
        // This catches orders that are fully allocated regardless of their database status
        params.fully_allocated = 'true';
      } else if (statusFilter === 'overdue') {
        params.overdue = 'true';
      } else if (statusFilter === 'in_production') {
        params.status = 'in_production';
      } else if (statusFilter === 'partially_shipped') {
        params.status = 'partially_shipped';
      } else if (statusFilter === 'complete') {
        params.status = 'complete';
      } else if (statusFilter !== 'active') {
        params.status = statusFilter;
      }

      // Add shipped date filter (format as YYYY-MM-DD)
      if (shippedDateFilter) {
        const year = shippedDateFilter.getFullYear();
        const month = String(shippedDateFilter.getMonth() + 1).padStart(2, '0');
        const day = String(shippedDateFilter.getDate()).padStart(2, '0');
        params.shipped_date = `${year}-${month}-${day}`;
      }

      // Add due date filter (format as YYYY-MM-DD)
      if (dueDateFilter) {
        const year = dueDateFilter.getFullYear();
        const month = String(dueDateFilter.getMonth() + 1).padStart(2, '0');
        const day = String(dueDateFilter.getDate()).padStart(2, '0');
        params.due_date = `${year}-${month}-${day}`;
      }

      // Apply fully_allocated filter when showReadyOnly checkbox is checked
      if (showReadyOnly) {
        params.fully_allocated = 'true';
      }

      // Apply sort ordering for complete tab
      if (statusFilter === 'complete' && completeSortBy !== 'order_date') {
        params.ordering = `-${completeSortBy}`;
      }

      const response = await manufacturingAPI.customerOrders.list(params);
      let ordersData = response.data.results || [];
      
      setOrders(ordersData);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await manufacturingAPI.customerOrders.dashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleExportDownload = async () => {
    const selected = Object.keys(exportStatuses).filter(k => exportStatuses[k]);
    const selectedCols = EXPORT_COLUMNS.map(c => c.key).filter(k => exportColumns[k]);
    if (selected.length === 0) {
      toast.error('Select at least one status to export');
      return;
    }
    if (selectedCols.length === 0) {
      toast.error('Select at least one column to include');
      return;
    }
    if (!exportStartDate || !exportEndDate) {
      toast.error('Pick a start and end date');
      return;
    }
    if (exportEndDate < exportStartDate) {
      toast.error('End date must be on or after start date');
      return;
    }

    setExporting(true);
    try {
      const response = await manufacturingAPI.reports.downloadDispatchExport({
        start_date: exportStartDate,
        end_date: exportEndDate,
        statuses: selected,
        include_overdue: exportIncludeOverdue,
        columns: selectedCols,
        date_mode: exportDateMode,
      });

      authAPI.updatePreferences({
        dispatchExport: {
          statuses: exportStatuses,
          columns: exportColumns,
          include_overdue: exportIncludeOverdue,
          date_mode: exportDateMode,
        },
      }).catch(() => {});

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Dispatch_${exportStartDate.replace(/-/g, '')}_to_${exportEndDate.replace(/-/g, '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Dispatch export downloaded');
      setExportOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      const msg = error?.response?.data?.error || 'Failed to generate export';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
    sessionStorage.removeItem('dispatch_page');
  };

  const handleOrderNumberChange = (event) => {
    setOrderNumberQuery(event.target.value);
    setPage(1);
    sessionStorage.removeItem('dispatch_page');
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
    sessionStorage.removeItem('dispatch_page');
  };

  const handleShippedDateChange = (newDate) => {
    setShippedDateFilter(newDate);
    setCalendarAnchorEl(null);
    setPage(1);
    sessionStorage.removeItem('dispatch_page');
    // Auto-switch to complete filter when selecting a shipped date
    if (newDate && statusFilter !== 'complete') {
      setStatusFilter('complete');
    }
  };

  const handleDueDateChange = (newDate) => {
    setDueDateFilter(newDate);
    setDueDateAnchorEl(null);
    setPage(1);
    sessionStorage.removeItem('dispatch_page');
    // Persist to user preferences
    const dateStr = newDate ? newDate.toISOString() : null;
    authAPI.updatePreferences({ dispatchDueDateFilter: dateStr }).catch(() => {});
  };

  const clearFilters = () => {
    setShowReadyOnly(false);
    setStatusFilter('active');
    setSearchQuery('');
    setOrderNumberQuery('');
    setShippedDateFilter(null);
    setDueDateFilter(null);
    setCompleteSortBy('order_date');
    setPage(1);
    sessionStorage.removeItem('dispatch_page');
    authAPI.updatePreferences({ dispatchDueDateFilter: null, dispatchCompleteSortBy: 'order_date' }).catch(() => {});
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem('dispatch_page', String(newPage));
  };

  const handleDispatchNotesSaved = useCallback((orderId, notes) => {
    setOrders(prev => prev.map(order => 
      order.id === orderId ? { ...order, dispatch_notes: notes } : order
    ));
  }, []);

  const getShippableQuantity = (order) => {
    if (!order.lines) return 0;
    // quantity_allocated already represents only the current remaining allocation
    // (shipping decrements allocations), so allocated IS the shippable amount
    return order.lines.reduce((total, line) => {
      return total + (line.quantity_allocated || 0);
    }, 0);
  };

  const getProductionProgress = (order) => {
    if (!order.lines || order.lines.length === 0) return 0;

    const totalOrdered = order.lines.reduce((sum, line) => sum + (line.quantity_ordered || 0), 0);
    const totalAllocated = order.lines.reduce((sum, line) => sum + (line.quantity_allocated || 0), 0);
    const totalShipped = order.lines.reduce((sum, line) => sum + (line.quantity_shipped || 0), 0);

    if (totalOrdered === 0) return 0;
    // allocated is current remaining allocation (shipped items already removed), so total progress = shipped + allocated
    return Math.min(100, ((totalAllocated + totalShipped) / totalOrdered) * 100);
  };

  // Check if ALL lines in an order are fully allocated (accounting for already-shipped quantities)
  const isOrderFullyAllocated = (order) => {
    if (!order.lines || order.lines.length === 0) return false;

    return order.lines.every(line => {
      const allocated = line.quantity_allocated || 0;
      const ordered = line.quantity_ordered || 0;
      const shipped = line.quantity_shipped || 0;
      const remaining = ordered - shipped;
      // For partially shipped orders, check if the remaining unshipped quantity is fully allocated
      return remaining <= 0 || allocated >= remaining;
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatShippedDateDisplay = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'info',
      'confirmed': 'primary',
      'in_production': 'warning',
      'ready_to_ship': 'success',
      'partially_shipped': 'info',
      'complete': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'New',
      'confirmed': 'Confirmed',
      'in_production': 'In Production',
      'ready_to_ship': 'Ready to Ship',
      'partially_shipped': 'Partially Shipped',
      'complete': 'Complete',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const hasActiveFilters = showReadyOnly || statusFilter !== 'active' || searchQuery.trim() || orderNumberQuery.trim() || shippedDateFilter || dueDateFilter;
  const totalPages = Math.ceil(totalCount / pageSize);
  const calendarOpen = Boolean(calendarAnchorEl);
  const dueDateCalendarOpen = Boolean(dueDateAnchorEl);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShipping color="primary" />
              Dispatch Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Manage order dispatch and shipping
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => setExportOpen(true)}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                loadOrders();
                loadStats();
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Export Dialog */}
        <Dialog
          open={exportOpen}
          onClose={() => !exporting && setExportOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileDownload color="primary" />
            Export Dispatch to Excel
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a date range, choose whether to filter by due date or shipped date, and pick the statuses to include. One row per line item.
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter dates by</Typography>
              <ToggleButtonGroup
                value={exportDateMode}
                exclusive
                onChange={(e, val) => { if (val) setExportDateMode(val); }}
                size="small"
                fullWidth
              >
                <ToggleButton value="due_date">Due Date</ToggleButton>
                <ToggleButton value="completed_date">Shipped / Complete Date</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Statuses to include</Typography>
                <FormGroup>
                  {[
                    { key: 'ready_to_ship', label: 'Ready to Ship' },
                    { key: 'in_production', label: 'In Production' },
                    { key: 'partially_shipped', label: 'Partially Shipped' },
                    { key: 'complete', label: 'Shipped / Complete' },
                    { key: 'confirmed', label: 'Confirmed' },
                    { key: 'new', label: 'New' },
                    { key: 'cancelled', label: 'Cancelled' },
                  ].map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          size="small"
                          checked={!!exportStatuses[key]}
                          onChange={(e) =>
                            setExportStatuses(prev => ({ ...prev, [key]: e.target.checked }))
                          }
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Columns to include</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => setExportColumns(DEFAULT_EXPORT_COLUMNS)}
                      sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}
                    >
                      All
                    </Button>
                    <Button
                      size="small"
                      onClick={() =>
                        setExportColumns(EXPORT_COLUMNS.reduce((a, c) => ({ ...a, [c.key]: false }), {}))
                      }
                      sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}
                    >
                      None
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Order # is always included.
                </Typography>
                <FormGroup>
                  {EXPORT_COLUMNS.map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          size="small"
                          checked={!!exportColumns[key]}
                          onChange={(e) =>
                            setExportColumns(prev => ({ ...prev, [key]: e.target.checked }))
                          }
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>

            {exportDateMode === 'due_date' && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportIncludeOverdue}
                      onChange={(e) => setExportIncludeOverdue(e.target.checked)}
                      color="error"
                    />
                  }
                  label="Include overdue orders still awaiting dispatch (due before start date)"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportOpen(false)} disabled={exporting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
              onClick={handleExportDownload}
              disabled={exporting}
            >
              {exporting ? 'Generating…' : 'Download'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                border: statusFilter === 'active' ? 2 : 0,
                borderColor: 'primary.main'
              }}
              onClick={() => setStatusFilter('active')}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Active Orders</Typography>
                <Typography variant="h4">{stats.total_active}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Awaiting dispatch
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{ 
                cursor: 'pointer',
                border: statusFilter === 'ready_to_ship' ? 2 : 0,
                borderColor: 'success.main',
                bgcolor: 'success.50'
              }}
              onClick={() => setStatusFilter('ready_to_ship')}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Ready to Ship</Typography>
                <Typography variant="h4" color="success.main">{stats.ready_to_ship}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  All items allocated
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{ 
                cursor: 'pointer',
                border: statusFilter === 'in_production' ? 2 : 0,
                borderColor: 'warning.main'
              }}
              onClick={() => setStatusFilter('in_production')}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>In Production</Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.in_production || (stats.total_active - stats.ready_to_ship - (stats.partially_shipped || 0))}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Being manufactured
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{ 
                cursor: 'pointer',
                border: statusFilter === 'overdue' ? 2 : 0,
                borderColor: 'error.main'
              }}
              onClick={() => setStatusFilter('overdue')}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Overdue</Typography>
                <Typography variant="h4" color="error.main">{stats.overdue}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Past due date
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{ 
                cursor: 'pointer',
                border: statusFilter === 'complete' ? 2 : 0,
                borderColor: 'grey.500',
                bgcolor: statusFilter === 'complete' ? 'grey.100' : 'inherit'
              }}
              onClick={() => setStatusFilter('complete')}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Shipped / Complete</Typography>
                <Typography variant="h4" color="text.secondary">{stats.complete || 0}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Fully dispatched
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Alerts */}
        {stats.ready_to_ship > 0 && statusFilter !== 'complete' && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<LocalShipping />}>
            <strong>{stats.ready_to_ship} orders are ready to ship!</strong> All items allocated - click on an order to dispatch.
          </Alert>
        )}

        {stats.overdue > 0 && statusFilter !== 'complete' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{stats.overdue} orders are overdue!</strong> Prioritize these for dispatch.
          </Alert>
        )}

        {statusFilter === 'complete' && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<Done />}>
            Viewing completed/shipped orders
            {shippedDateFilter && (
              <strong> - filtered by dispatch date: {formatShippedDateDisplay(shippedDateFilter)}</strong>
            )}
          </Alert>
        )}

        {/* Search and Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FilterList color="primary" />
              <Typography variant="h6">Search & Filters</Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Order # only..."
                  value={orderNumberQuery}
                  onChange={handleOrderNumberChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: orderNumberQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setOrderNumberQuery('');
                            setPage(1);
                          }}
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search orders, customers, products..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchQuery('');
                            setPage(1);
                          }}
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={handleStatusChange}
                  >
                    <MenuItem value="active">All Active</MenuItem>
                    <MenuItem value="ready_to_ship">Ready to Ship</MenuItem>
                    <MenuItem value="in_production">In Production</MenuItem>
                    <MenuItem value="partially_shipped">Partially Shipped</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="new">New</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem divider />
                    <MenuItem value="complete">Shipped / Complete</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                {statusFilter === 'complete' ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={completeSortBy}
                      label="Sort By"
                      onChange={(e) => {
                        const newSort = e.target.value;
                        setCompleteSortBy(newSort);
                        setPage(1);
                        authAPI.updatePreferences({ dispatchCompleteSortBy: newSort }).catch(() => {});
                      }}
                    >
                      <MenuItem value="order_date">Order Date</MenuItem>
                      <MenuItem value="completed_at">Recently Completed</MenuItem>
                      <MenuItem value="shipped_at">Recently Shipped</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showReadyOnly}
                        onChange={(e) => {
                          setShowReadyOnly(e.target.checked);
                          setPage(1);
                        }}
                        color="success"
                      />
                    }
                    label="Show Ready to Ship Only"
                  />
                )}
              </Grid>

              <Grid item xs={12} md={2}>
                {hasActiveFilters && (
                  <Button
                    fullWidth
                    size="small"
                    startIcon={<Clear />}
                    onClick={clearFilters}
                    variant="outlined"
                  >
                    Clear All
                  </Button>
                )}
              </Grid>
            </Grid>

            {dueDateFilter && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Chip
                  icon={<CalendarMonth />}
                  label={`Due Date: ${formatShippedDateDisplay(dueDateFilter)}`}
                  color="success"
                  onDelete={() => {
                    setDueDateFilter(null);
                    setPage(1);
                    authAPI.updatePreferences({ dispatchDueDateFilter: null }).catch(() => {});
                  }}
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing <strong>{orders.length}</strong> of <strong>{totalCount}</strong> orders
                {statusFilter === 'complete' && ' (completed)'}
                {statusFilter === 'overdue' && ' (overdue)'}
                {shippedDateFilter && ` - dispatched on ${formatShippedDateDisplay(shippedDateFilter)}`}
                {dueDateFilter && ` - due on ${formatShippedDateDisplay(dueDateFilter)}`}
              </Typography>
              {loading && <CircularProgress size={20} />}
            </Box>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <TableContainer component={Paper}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1250 }}>
            <colgroup>
              <col style={{ width: 100 }} />  {/* Order # */}
              <col style={{ width: 130 }} />  {/* Customer */}
              <col />                          {/* Items - flexible */}
              <col style={{ width: 80 }} />   {/* Progress / Shipped */}
              <col style={{ width: 95 }} />   {/* Ready / Completed */}
              <col style={{ width: 115 }} />  {/* Due Date */}
              <col style={{ width: 120 }} />  {/* Status */}
              <col style={{ width: 150 }} />  {/* Consignment / Tracking */}
              <col style={{ width: 95 }} />   {/* Service */}
              <col style={{ width: 250 }} />  {/* Notes */}
              <col style={{ width: 110 }} />  {/* Actions */}
            </colgroup>
            <TableHead>
              <TableRow sx={{ bgcolor: statusFilter === 'complete' ? 'grey.600' : statusFilter === 'overdue' ? 'error.main' : 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 100 }}>Order #</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 120 }}>Customer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 180 }}>Items</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 80 }} align="center">
                  {statusFilter === 'complete' ? 'Shipped' : 'Progress'}
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 70 }} align="center">
                  {statusFilter === 'complete' ? 'Completed' : 'Ready'}
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    width: 85,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)'
                    },
                    userSelect: 'none',
                    position: 'relative',
                    ...(dueDateFilter && {
                      bgcolor: statusFilter === 'overdue' ? 'error.dark' : 'success.dark',
                      borderBottom: '3px solid',
                      borderBottomColor: statusFilter === 'overdue' ? 'error.light' : 'success.light',
                    }),
                  }}
                  onClick={(e) => {
                    setDueDateAnchorEl(e.currentTarget);
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Due Date
                      <CalendarMonth fontSize="small" sx={{ opacity: 0.8 }} />
                    </Box>

                    {dueDateFilter && (
                      <Tooltip title="Clear due date filter">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDueDateFilter(null);
                            setPage(1);
                            authAPI.updatePreferences({ dispatchDueDateFilter: null }).catch(() => {});
                          }}
                          sx={{ color: 'white', p: 0.25 }}
                        >
                          <Clear sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Due Date Calendar Popover */}
                  <Popover
                    open={dueDateCalendarOpen}
                    anchorEl={dueDateAnchorEl}
                    onClose={() => setDueDateAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box sx={{ p: 1 }}>
                      <DateCalendar
                        value={dueDateFilter}
                        onChange={(newDate) => {
                          handleDueDateChange(newDate);
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pb: 1 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setDueDateFilter(null);
                            setDueDateAnchorEl(null);
                            setPage(1);
                            authAPI.updatePreferences({ dispatchDueDateFilter: null }).catch(() => {});
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            handleDueDateChange(new Date());
                          }}
                        >
                          Today
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </TableCell>
                
                {/* Status column with calendar date picker */}
                <TableCell 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold', 
                    width: 120,
                    cursor: statusFilter === 'complete' ? 'pointer' : 'default',
                    '&:hover': statusFilter === 'complete' ? { 
                      bgcolor: 'rgba(255,255,255,0.1)' 
                    } : {},
                    userSelect: 'none',
                    position: 'relative',
                  }}
                  onClick={(e) => {
                    if (statusFilter === 'complete') {
                      setCalendarAnchorEl(e.currentTarget);
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Status
                      {statusFilter === 'complete' && (
                        <CalendarMonth fontSize="small" sx={{ opacity: 0.8 }} />
                      )}
                    </Box>
                    
                    {shippedDateFilter && (
                      <Tooltip title="Clear date filter">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShippedDateFilter(null);
                            setPage(1);
                          }}
                          sx={{ color: 'white', p: 0.25 }}
                        >
                          <Clear sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  
                  {/* Calendar Popover - shows calendar directly */}
                  <Popover
                    open={calendarOpen}
                    anchorEl={calendarAnchorEl}
                    onClose={() => setCalendarAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box sx={{ p: 1 }}>
                      <DateCalendar
                        value={shippedDateFilter}
                        onChange={(newDate) => {
                          handleShippedDateChange(newDate);
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pb: 1 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setShippedDateFilter(null);
                            setCalendarAnchorEl(null);
                            setPage(1);
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            handleShippedDateChange(new Date());
                          }}
                        >
                          Today
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </TableCell>
                
                <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 120 }}>Consignment / Tracking</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 100 }}>Service</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 250 }}>Notes</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 120 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ opacity: loading && orders.length > 0 ? 0.45 : 1, transition: 'opacity 0.15s' }}>
              {loading && orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <LocalShipping sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      {searchQuery
                        ? `No orders found matching "${searchQuery}"`
                        : shippedDateFilter
                        ? `No orders dispatched on ${formatShippedDateDisplay(shippedDateFilter)}`
                        : dueDateFilter
                        ? `No orders due on ${formatShippedDateDisplay(dueDateFilter)}`
                        : statusFilter === 'complete'
                        ? 'No completed orders found'
                        : statusFilter === 'overdue'
                        ? 'No overdue orders found'
                        : 'No orders found'
                      }
                    </Typography>
                    {hasActiveFilters && (
                      <Button
                        size="small"
                        startIcon={<Clear />}
                        onClick={clearFilters}
                        sx={{ mt: 2 }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => {
                  const shippableQty = getShippableQuantity(order);
                  const productionProgress = getProductionProgress(order);
                  const isOverdue = order.days_until_due < 0;
                  const isComplete = order.status === 'complete';
                  
                  // Check if ALL lines are fully allocated
                  const allLinesFullyAllocated = isOrderFullyAllocated(order);
                  
                  // Only show as "ready to ship" if ALL lines are allocated AND there are items to ship
                  const hasItemsToShip = allLinesFullyAllocated && shippableQty > 0;

                  return (
                    <TableRow 
                      key={order.id}
                      sx={{ 
                        '&:hover': { backgroundColor: 'action.hover' },
                        borderLeft: isComplete ? '4px solid' : isOverdue ? '4px solid' : hasItemsToShip ? '4px solid' : '4px solid transparent',
                        borderLeftColor: isComplete ? 'grey.500' : isOverdue ? 'error.main' : hasItemsToShip ? 'success.main' : 'transparent',
                        bgcolor: isComplete ? 'grey.50' : hasItemsToShip ? 'success.50' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.order_number}
                        </Typography>
                        {order.customer_reference && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Ref: {order.customer_reference}
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.customer_name}
                        </Typography>
                        {order.delivery_postcode && (
                          <Typography variant="caption" color="text.secondary">
                            {order.delivery_postcode}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {order.lines?.map((line, idx) => {
                          const lineAllocated = line.quantity_allocated || 0;
                          const lineOrdered = line.quantity_ordered || 0;
                          const lineFullyAllocated = lineAllocated >= lineOrdered;

                          return (
                            <Box key={line.id} sx={{ mb: idx < order.lines.length - 1 ? 0.5 : 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="bold" noWrap sx={{ maxWidth: 500 }}>
                                  {line.product_name}
                                </Typography>
                                <Chip
                                  label={`x${line.quantity_ordered}`}
                                  size="small"
                                  color={lineFullyAllocated ? 'success' : 'primary'}
                                  variant={lineFullyAllocated ? 'filled' : 'outlined'}
                                  sx={{ height: 18, fontSize: '0.7rem' }}
                                />
                                {!isComplete && !lineFullyAllocated && (
                                  <Chip
                                    label={`${lineAllocated}/${lineOrdered}`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 16, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                        {order.special_codes
                          ?.filter(sc => sc.code_type_display === 'STOCKITEM')
                          .map((sc, idx) => (
                            <Box key={`stockitem-${sc.id ?? idx}`} sx={{ mt: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  label="STOCKITEM"
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                                <Typography variant="caption" fontWeight="bold" noWrap sx={{ maxWidth: 500 }}>
                                  {sc.value}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                      </TableCell>

                      <TableCell align="center">
                        {isComplete ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                            <CheckCircle color="success" fontSize="small" />
                            <Typography variant="caption" color="success.main" fontWeight="bold">
                              100%
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={productionProgress} 
                              sx={{ width: 50, height: 6, borderRadius: 3 }}
                              color={productionProgress >= 100 ? 'success' : 'primary'}
                            />
                            <Typography variant="caption" fontWeight="bold">
                              {Math.round(productionProgress)}%
                            </Typography>
                          </Box>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {isComplete ? (
                          <Chip 
                            label="Shipped"
                            color="success"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ) : hasItemsToShip ? (
                          <Chip 
                            label={`${shippableQty}`}
                            color="success"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ) : shippableQty > 0 ? (
                          <Tooltip title="Some items ready but not all lines are fully allocated">
                            <Chip 
                              label="Partial"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip 
                            label="Waiting"
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography 
                          variant="caption" 
                          color={isComplete ? 'text.secondary' : isOverdue ? 'error.main' : 'text.primary'}
                          fontWeight={isOverdue && !isComplete ? 'bold' : 'normal'}
                        >
                          {formatDate(order.due_date)}
                        </Typography>
                        {!isComplete && (
                          <Typography variant="caption" display="block" color={isOverdue ? 'error.main' : 'text.secondary'}>
                            {order.days_until_due < 0 
                              ? `${Math.abs(order.days_until_due)}d late`
                              : order.days_until_due === 0
                              ? 'Today'
                              : `${order.days_until_due}d`
                            }
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip 
                          label={getStatusLabel(order.status)} 
                          color={getStatusColor(order.status)}
                          size="small"
                          variant={isComplete ? 'outlined' : 'filled'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        {isComplete && (() => {
                          const shippedConsignments = order.dx_consignments?.filter(c => c.shipped_at && c.status !== 'deleted') || [];
                          
                          if (shippedConsignments.length > 0) {
                            const latestShipped = shippedConsignments.reduce((latest, c) => 
                              new Date(c.shipped_at) > new Date(latest.shipped_at) ? c : latest
                            );
                            return (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {formatDate(latestShipped.shipped_at)}
                              </Typography>
                            );
                          }
                          
                          if (order.completed_at) {
                            return (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {formatDate(order.completed_at)}
                              </Typography>
                            );
                          }
                          
                          return null;
                        })()}
                      </TableCell>

                      <TableCell>
                        <ConsignmentTrackingCell consignments={order.dx_consignments} />
                      </TableCell>

                      <TableCell>
                        <DeliveryServiceSelect
                          orderId={order.id}
                          initialValue={order.delivery_service}
                          onSave={(orderId, newValue) => {
                            setOrders(prev => prev.map(o => 
                              o.id === orderId ? { ...o, delivery_service: newValue } : o
                            ));
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <DispatchNotesField
                          orderId={order.id}
                          initialValue={order.dispatch_notes}
                          onSave={handleDispatchNotesSaved}
                          canEdit={canEditDispatchNotes}
                        />
                        {order.special_codes && order.special_codes.length > 0 && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                            {order.special_codes
                              .filter(sc => sc.code_type_display === 'BOOKIN')
                              .map((sc, idx) => (
                                <Typography 
                                  key={idx} 
                                  variant="caption" 
                                  display="block" 
                                  sx={{ 
                                    color: 'info.main',
                                    fontSize: '0.7rem',
                                    lineHeight: 1.3
                                  }}
                                >
                                  {sc.value}
                                </Typography>
                              ))}
                          </Box>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="View Order">
                            <IconButton 
                              size="small"
                              color="primary"
                              onClick={() => { saveScrollPosition(); navigate(`/dispatch/orders/${order.id}`); }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {isComplete ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => { saveScrollPosition(); navigate(`/dispatch/orders/${order.id}/ship`); }}
                              sx={{ minWidth: 80, fontSize: '0.75rem', py: 0.5 }}
                            >
                              View Shipping
                            </Button>
                          ) : (
                            <Tooltip title={hasItemsToShip ? "Ship Order" : shippableQty > 0 ? "Not all items allocated yet" : "No items ready"}>
                              <span>
                                <Button
                                  size="small"
                                  variant={hasItemsToShip ? "contained" : "outlined"}
                                  color={hasItemsToShip ? "success" : "inherit"}
                                  onClick={() => { saveScrollPosition(); navigate(`/dispatch/orders/${order.id}/ship`); }}
                                  disabled={!hasItemsToShip}
                                  sx={{ minWidth: 60, fontSize: '0.75rem', py: 0.5 }}
                                >
                                  Ship
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}